// ═══════════════════════════════════════════════════════════════════════════
// hosts.js — single source of truth for which surface lives at which host.
//
// Apex (meetepio.com / www.meetepio.com) → public landing page (LandingV2).
// app.meetepio.com                       → authenticated app.
// Legacy getepisode.ai / www.getepisode.ai → treated as the app host so the
//   migration window doesn't strand existing inbound traffic.
// localhost + Vercel preview URLs        → app host (dev/preview default).
//
// Auth flow rule: after sign-in we want users on the app subdomain. OAuth
// and magic-link `redirectTo` values flow through `authRedirectBase()`
// below. The Supabase client's storage is scoped to `.meetepio.com` so
// the session survives the apex ↔ subdomain hop without a re-login.
// ═══════════════════════════════════════════════════════════════════════════

export const APP_HOST     = process.env.REACT_APP_APP_HOST     || 'app.meetepio.com';
export const LANDING_HOST = process.env.REACT_APP_LANDING_HOST || 'meetepio.com';

// Hostnames we treat as the public-facing landing surface.
const LANDING_HOSTS = new Set([
  LANDING_HOST,
  `www.${LANDING_HOST}`,
  // Legacy apex during the meetepio.com migration. Remove once DNS is fully
  // cut over and the old domain stops serving traffic.
  'getepisode.ai',
  'www.getepisode.ai',
]);

function currentHostname() {
  if (typeof window === 'undefined') return '';
  return (window.location.hostname || '').toLowerCase();
}

/** True when the page is being served from `app.meetepio.com` (or any
 *  subdomain prefixed with `app.`). Vercel preview URLs and localhost
 *  return false — those don't carry an `app.` prefix and the auth-state
 *  router handles them. */
export function isAppSubdomain() {
  const h = currentHostname();
  return h.startsWith('app.') || h === APP_HOST;
}

/** True when the page is being served from the public landing host
 *  (apex meetepio.com, www, or the legacy getepisode.ai pair). */
export function isLandingHost() {
  const h = currentHostname();
  return LANDING_HOSTS.has(h);
}

/** True when we're running on a real meetepio.com host (apex, www, or
 *  app subdomain). Used to gate cookie-domain behavior and the apex ↔
 *  subdomain redirect — localhost and Vercel previews must keep using
 *  per-origin localStorage and the auth-state-only router. */
export function isMeetepioHost() {
  const h = currentHostname();
  return h === LANDING_HOST || h === `www.${LANDING_HOST}` || h === APP_HOST || h.endsWith(`.${LANDING_HOST}`);
}

/** The base origin auth flows should land on after sign-in. In production
 *  this is the app subdomain so the user's session lands directly on the
 *  authenticated surface; in dev / preview / legacy it's the current
 *  origin so flows keep working without DNS for app.* */
export function authRedirectBase() {
  if (typeof window === 'undefined') return `https://${APP_HOST}`;
  if (isMeetepioHost()) return `https://${APP_HOST}`;
  return window.location.origin;
}

/** Where to send a signed-in user who lands on the public apex.
 *  Returns an absolute URL preserving the current path + query, or null
 *  when no redirect is needed (already on the app host, or in dev). */
export function appUrlForCurrentPath() {
  if (typeof window === 'undefined') return null;
  const path = window.location.pathname + window.location.search + window.location.hash;
  return `https://${APP_HOST}${path}`;
}

/** Mirror for the reverse: where to send a logged-out visitor who landed
 *  on app.meetepio.com but should see the landing first. */
export function landingUrlForCurrentPath() {
  if (typeof window === 'undefined') return null;
  const path = window.location.pathname + window.location.search + window.location.hash;
  return `https://${LANDING_HOST}${path}`;
}

